import Footer from 'components/Footer';
import Header from 'components/Header';
import { BaseButton } from 'components/Shared/Buttons';
import { fonts, screen } from 'components/styles/variables';
import VehicleViewModel from 'lib/Presenters/VdpPresenter/Vehicle';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import ApiResponse from 'types/Apis/VehicleApiResponse';
import { ExperienceApiResponse } from 'types/Apis/VehicleWithExperience';
import useSimilarListings from 'lib/hooks/useSimilarListingsViewModels';
import ListingCard from 'components/Shared/ListingsCarousel/ListingCard';
import { useEffect, useState } from 'react';
import { useSrpFilters } from 'lib/hooks/useSrpFilters';
import { transformSrpFilterApiParamsToState } from 'lib/srpFilterTransformers';

interface InactiveListingProps {
  vehicle?: ApiResponse | null;
  experience?: ExperienceApiResponse;
}

function InactiveListing({ vehicle, experience }: InactiveListingProps): JSX.Element {
  const { t } = useTranslation();
  const router = useRouter();
  const { asPath } = router;
  const [params, setParams] = useState<URLSearchParams | null>(null);
  const { setValues } = useSrpFilters();
  let viewModel;
  if (vehicle && experience) viewModel = new VehicleViewModel(vehicle, experience);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const currentHash = window.location.hash.substring(1);
      const newParams = new URLSearchParams(currentHash);
      newParams.delete('vin');

      if (vehicle?.make) newParams.set('make', vehicle.make);
      if (vehicle?.model) newParams.set('model', vehicle.model);
      if (vehicle?.year) newParams.set('year_min', vehicle.year.toString());
      if (vehicle?.year) newParams.set('year_max', vehicle.year.toString());

      setParams(newParams);
    }
  }, [vehicle]);

  const similarListings = useSimilarListings(vehicle?.vin || '');
  const hasSimilarListings = similarListings.length > 0;
  const displayListings = similarListings.slice(0, 4);

  function handleNavigateToListings(): void {
    if (params) {
      const filterParams = {
        make: vehicle?.make,
        model: vehicle?.model,
        year_min: vehicle?.year?.toString(),
        year_max: vehicle?.year?.toString(),
      };

      const filterState = transformSrpFilterApiParamsToState(filterParams);
      setValues(filterState);

      router.push(`/listings#${params.toString()}`);
    }
  }

  return (
    <>
      <Header url={asPath} includeAds={true} isVdp={true} vdpUrl={viewModel?.vdpUrl} />
      <div className="inactive-listing-container">
        <h4 className="inactive-listing-header">{t('vdp.inactiveListing.header')}</h4>
        <p className="inactive-listing-subText">{t('vdp.inactiveListing.subText')}</p>

        {/* Similar Listings Grid */}
        {hasSimilarListings && (
          <div className="similar-listings-container">
            <div className="similar-listings-grid">
              {displayListings.map((listing) => (
                <ListingCard key={listing.vdpUrl} {...listing} />
              ))}
            </div>
          </div>
        )}

        <BaseButton
          normalText={t('vdp.inactiveListing.cta')}
          className="inactive-listing-cta"
          style={{ height: '56px', width: '288px' }}
          onClick={handleNavigateToListings}
        />
      </div>
      <Footer />
      <style jsx>{`
        .inactive-listing-container {
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          margin-bottom: 302px;
        }

        .inactive-listing-icon {
          margin-top: 85.25px;
        }

        @media (min-width: ${screen.lgMin}) {
          .inactive-listing-icon {
            margin-top: 192px;
          }

          .inactive-listing-cta {
            width: 268px !important;
          }
        }

        .inactive-listing-header {
          color: var(--Core-Black, #333);
          text-align: center;
          font-feature-settings: 'liga' off, 'clig' off;
          font-family: ${fonts.default};
          font-size: 20px;
          font-style: normal;
          font-weight: 700;
          line-height: 28px;
          letter-spacing: 0.2px;
          margin-bottom: 8px;
          padding-top: 24px;
        }

        @media (min-width: ${screen.lgMin}) {
          .inactive-listing-header {
            margin-top: 118px;
          }
        }

        .inactive-listing-subText {
          color: var(--Core-Grey-2, #888);
          text-align: center;
          font-feature-settings: 'liga' off, 'clig' off;
          font-family: ${fonts.default};
          font-size: 18px;
          font-style: normal;
          font-weight: 400;
          line-height: 24px;
          letter-spacing: 0.2px;
          margin-top: 0px;
          margin-bottom: 24px;
          width: 280px;
        }

        @media (min-width: ${screen.smMin}) {
          .inactive-listing-subText {
            width: auto;
            margin-bottom: 48px;
          }
        }

        .similar-listings-container {
          width: 100%;
          max-width: 1200px;
          margin-bottom: 48px;
          padding: 0 16px;
        }

        .similar-listings-grid {
          display: flex;
          flex-direction: row;
          gap: 24px;
          overflow-x: auto;
          padding-bottom: 16px;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
          -ms-overflow-style: none;
          width: 100%;
        }

        .similar-listings-grid::-webkit-scrollbar {
          display: none;
        }

        .similar-listings-grid > * {
          flex: 0 0 272px;
        }

        @media (min-width: ${screen.smMin}) {
          .similar-listings-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            overflow-x: visible;
            padding-bottom: 0;
          }

          .similar-listings-grid > * {
            flex: none;
          }
        }

        @media (min-width: ${screen.lgMin}) {
          .similar-listings-grid {
            grid-template-columns: repeat(4, 1fr);
          }
        }
      `}</style>
    </>
  );
}

export default InactiveListing;
